import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, LocateFixed } from "lucide-react";
import { toast } from "sonner";
import { getCity, setCity } from "@/lib/city";
import { useLanguage } from "@/i18n/LanguageContext";

export const SearchBar = ({ cities = [], initialQuery = "", onSearch, placeholder = "Ex. : restaurant, karaoké, événement, bar…" }) => {
  const [query, setQuery] = useState(initialQuery);
  const [city, setCityState] = useState(getCity());
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  useEffect(() => {
    const sync = () => setCityState(getCity());
    window.addEventListener("gospot:citychange", sync);
    return () => window.removeEventListener("gospot:citychange", sync);
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (onSearch) {
      onSearch(query, city);
      return;
    }
    navigate(`/explorer?q=${encodeURIComponent(query)}&ville=${encodeURIComponent(city)}`);
  };

  const geolocate = () => {
    if (!navigator.geolocation) {
      toast.error(t("La géolocalisation n'est pas disponible sur cet appareil"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      () => toast.success(t("Position détectée — Québec, QC")),
      () => toast.error(t("Impossible de détecter ta position"))
    );
  };

  return (
    <form
      data-testid="search-bar"
      onSubmit={submit}
      className="mx-auto flex w-full max-w-3xl flex-col gap-2 rounded-3xl border border-slate-100 bg-white p-3 shadow-[0_12px_40px_rgba(15,23,42,0.12)] sm:flex-row sm:items-center sm:rounded-full sm:p-2 sm:pl-5"
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <Search className="h-5 w-5 shrink-0 text-slate-400" />
        <input
          data-testid="search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t(placeholder)}
          className="min-w-0 flex-1 bg-transparent py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400"
        />
      </div>
      <div className="flex min-w-0 items-center gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-1.5 border-slate-200 sm:border-l sm:pl-3">
          <MapPin className="h-4 w-4 shrink-0 text-[#2563EB]" />
          <select
            data-testid="search-city-select"
            value={city}
            onChange={(e) => {
              setCityState(e.target.value);
              setCity(e.target.value);
            }}
            className="min-w-0 max-w-full cursor-pointer truncate bg-transparent text-sm font-medium text-slate-700 outline-none"
          >
            {cities.map((c) => (
              <option key={c.slug} value={c.label}>
                {c.label}
              </option>
            ))}
            {!cities.length && <option value="Québec, QC">Québec, QC</option>}
          </select>
        </div>
        <button
          type="button"
          data-testid="geolocate-button"
          onClick={geolocate}
          aria-label={t("Utiliser ma position")}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-[#2563EB]"
        >
          <LocateFixed className="h-[18px] w-[18px]" />
        </button>
        <button
          type="submit"
          data-testid="search-submit-button"
          className="shrink-0 rounded-full bg-[#2563EB] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1D4ED8] active:scale-95 sm:px-6"
        >
          {t("Rechercher")}
        </button>
      </div>
    </form>
  );
};
